/**
 * CapabilityLandingPage — shared landing template for the 6 business
 * capabilities (Sprint 6 IA refactor).
 *
 * Consistent anatomy across every capability:
 *   1. Eyebrow + Title
 *   2. Purpose statement (the "why")
 *   3. Primary actions
 *   4. Live KPI tiles (honest — placeholder if data missing, never fabricated)
 *   5. Sub-capabilities grid
 *   6. Optional Recent activity
 *   7. Related capabilities
 */
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, Lock, Sparkles } from "lucide-react";

const NAVY = "#0A1F44";
const GOLD = "#C9A24A";
const TEAL = "#0D9488";

const STATUS_TAG = {
  live:        { label: "Live",        bg: "#D1FAE5", color: "#065F46" },
  beta:        { label: "Beta",        bg: "#DBEAFE", color: "#1E40AF" },
  placeholder: { label: "Phase 1",     bg: "#FEF3C7", color: "#92400E" },
  future:      { label: "Future",      bg: "#F1F5F9", color: "#475569" },
  mock:        { label: "Mock data",   bg: "#CCFBF1", color: "#0F766E" },
};

export default function CapabilityLandingPage({
  eyebrow,
  title,
  purpose,
  accentColor = NAVY,
  primaryActions = [],
  kpis = [],
  subCapabilities = [],
  recentActivity = null,
  relatedCapabilities = [],
  children,
}) {
  return (
    <div className="max-w-6xl mx-auto p-6 lg:p-10 space-y-8" data-testid={`capability-${slug(title)}`}>
      {/* Header ─────────────────────────────────────────── */}
      <header className="space-y-3">
        <div className="text-[10px] uppercase tracking-[0.22em] font-semibold" style={{ color: accentColor }}>
          {eyebrow}
        </div>
        <h1 className="font-display text-3xl lg:text-4xl font-semibold leading-tight" style={{ color: NAVY }}>
          {title}
        </h1>
        <p className="text-[15px] text-slate-600 max-w-3xl leading-relaxed" data-testid="capability-purpose">
          {purpose}
        </p>
      </header>

      {/* Primary actions ─────────────────────────────────── */}
      {primaryActions.length > 0 && (
        <div className="flex flex-wrap gap-2" data-testid="capability-primary-actions">
          {primaryActions.map((a, i) => (
            <ActionButton key={i} action={a} accentColor={accentColor} isPrimary={i === 0} />
          ))}
        </div>
      )}

      {/* KPI Tiles ───────────────────────────────────────── */}
      {kpis.length > 0 && (
        <section>
          <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
            Live signals
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {kpis.map((k, i) => <KpiTile key={i} kpi={k} accentColor={accentColor} />)}
          </div>
        </section>
      )}

      {/* Sub-capabilities grid ───────────────────────────── */}
      {subCapabilities.length > 0 && (
        <section>
          <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
            Sub-capabilities
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="capability-subs">
            {subCapabilities.map((s) => <SubCard key={s.name} sub={s} />)}
          </div>
        </section>
      )}

      {/* Recent activity slot ────────────────────────────── */}
      {recentActivity && (
        <section>
          <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
            Recent activity
          </div>
          {recentActivity}
        </section>
      )}

      {/* Free-form child slot for capability-specific extras */}
      {children}

      {/* Related capabilities ────────────────────────────── */}
      {relatedCapabilities.length > 0 && (
        <section className="border-t border-slate-100 pt-6">
          <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mb-2">
            Related capabilities
          </div>
          <div className="flex flex-wrap gap-2">
            {relatedCapabilities.map((r) => (
              <Link
                key={r.to}
                to={r.to}
                data-testid={`related-${slug(r.name)}`}
                className="inline-flex items-center gap-1.5 text-[12px] font-medium text-slate-600 border border-slate-200 rounded-full px-3 py-1.5 hover:bg-slate-50 hover:text-slate-900"
              >
                {r.name} <ArrowRight className="w-3 h-3" />
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

/* ─── Building blocks ─────────────────────────────────── */

function ActionButton({ action, accentColor, isPrimary }) {
  const cls = isPrimary
    ? "text-white shadow-sm"
    : "text-slate-700 bg-white border border-slate-200 hover:bg-slate-50";
  return (
    <Link
      to={action.to}
      data-testid={`action-${slug(action.label)}`}
      className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-[13px] font-semibold ${cls}`}
      style={isPrimary ? { background: accentColor } : {}}
    >
      {action.icon && <action.icon className="w-4 h-4" />}
      {action.label}
      {isPrimary && <ArrowRight className="w-3.5 h-3.5" />}
    </Link>
  );
}

function KpiTile({ kpi, accentColor }) {
  const isPlaceholder = kpi.value === null || kpi.value === undefined;
  return (
    <div
      data-testid={`kpi-${slug(kpi.label)}`}
      className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 space-y-1"
    >
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">
        {kpi.label}
      </div>
      <div className="font-display text-2xl font-semibold" style={{ color: isPlaceholder ? "#94A3B8" : accentColor }}>
        {isPlaceholder ? "—" : kpi.value}
      </div>
      <div className="text-[11px] text-slate-500">
        {isPlaceholder ? (kpi.emptyHint || "No data yet") : (kpi.subLabel || "")}
      </div>
    </div>
  );
}

function SubCard({ sub }) {
  const tag = sub.status ? STATUS_TAG[sub.status] : null;
  const disabled = sub.status === "future";
  const Icon = sub.icon;
  const Wrapper = disabled ? "div" : Link;
  const wrapperProps = disabled ? {} : { to: sub.to };
  return (
    <Wrapper
      {...wrapperProps}
      data-testid={`sub-${slug(sub.name)}`}
      className={`group rounded-2xl bg-white border border-slate-100 p-4 flex items-start gap-3 transition ${
        disabled ? "opacity-70 cursor-not-allowed" : "hover:border-slate-300 hover:shadow-sm cursor-pointer"
      }`}
    >
      <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: "#F1F5F9" }}>
        {Icon ? <Icon className="w-4 h-4" style={{ color: NAVY }} /> : <Sparkles className="w-4 h-4" style={{ color: NAVY }} />}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-semibold text-[14px]" style={{ color: NAVY }}>{sub.name}</div>
          {tag && (
            <span
              className="text-[9px] uppercase tracking-widest font-mono px-1.5 py-0.5 rounded"
              style={{ background: tag.bg, color: tag.color }}
            >
              {tag.label}
            </span>
          )}
          {disabled && <Lock className="w-3 h-3 text-slate-400" />}
        </div>
        {sub.description && (
          <div className="text-[12px] text-slate-500 leading-relaxed mt-1">{sub.description}</div>
        )}
      </div>
      {!disabled && (
        <div className="opacity-0 group-hover:opacity-100 transition self-center">
          {sub.external ? <ExternalLink className="w-3.5 h-3.5 text-slate-400" /> : <ArrowRight className="w-3.5 h-3.5 text-slate-400" />}
        </div>
      )}
    </Wrapper>
  );
}

function slug(s = "") {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export { NAVY, GOLD, TEAL };
